import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition, Overlay } from '@angular/cdk/overlay';
import { Subscription } from 'rxjs';
import { OrderService, OrderSupplyAllocation, OrderSupplyDetailsResponse, OrderSupplyLineDetails, OrderSupplyPendingReceiving, OrderSupplyResponse } from '../order.service';
import { deliveryDate, DeliveryPrecision } from './delivery-deadline.util';
import { findOrderSupplyLine, OrderSupplyProduct, OrderSupplySourceView, orderSupplySources, supplierSourceState, transferSourceState } from './order-supply-source.util';

let nextSupplyPopupId = 0;

@Component({
  selector: 'app-order-supply-source',
  templateUrl: './order-supply-source.component.html',
  styleUrls: ['./order-supply-source.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderSupplySourceComponent implements OnChanges, OnDestroy {
  @Input() orderId = 0;
  @Input() product!: OrderSupplyProduct;
  @Input() supply?: OrderSupplyResponse;
  @Input() cancelled = false;
  @Input() refreshVersion = 0;
  @Input() supplyLoading = false;
  @Input() supplyError = false;
  @ViewChild(CdkConnectedOverlay) connectedOverlay?: CdkConnectedOverlay;

  readonly popupId = `order-supply-popup-${++nextSupplyPopupId}`;
  readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 }
  ];
  readonly scrollStrategy = this.overlay.scrollStrategies.reposition();
  readonly transferState = transferSourceState;
  sources: OrderSupplySourceView[] = [];
  origin!: CdkOverlayOrigin;
  opened = false;
  loading = false;
  error = '';
  selectedSource = '';
  details?: OrderSupplyDetailsResponse;
  line?: OrderSupplyLineDetails;
  private trigger?: HTMLElement;
  private request?: Subscription;
  private generation = 0;
  private positionTimer?: ReturnType<typeof setTimeout>;

  constructor(private readonly orders: OrderService, private readonly overlay: Overlay, private readonly change: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.orderId && !changes.orderId.firstChange || changes.product &&
      changes.product.previousValue && changes.product.previousValue.orderProductId !== this.product?.orderProductId) {
      this.close(false);
      this.details = undefined;
      this.line = undefined;
    }
    if (changes.refreshVersion && !changes.refreshVersion.firstChange) {
      this.details = undefined;
      this.line = undefined;
      if (this.opened) this.loadDetails();
    } else if (changes.supply && !this.opened) {
      this.details = undefined;
      this.line = undefined;
    }
    this.rebuildSources();
  }

  open(origin: CdkOverlayOrigin, event: MouseEvent, source: OrderSupplySourceView) {
    event.stopPropagation();
    this.origin = origin;
    this.trigger = event.currentTarget as HTMLElement;
    this.selectedSource = source.id;
    this.opened = true;
    this.loadDetails();
  }

  close(restoreFocus = true) {
    this.opened = false;
    this.generation++;
    this.request?.unsubscribe();
    this.loading = false;
    clearTimeout(this.positionTimer);
    if (restoreFocus) {
      const target = this.trigger?.isConnected ? this.trigger : this.origin?.elementRef.nativeElement.querySelector('button');
      target?.focus();
    }
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close();
    }
  }

  loadDetails() {
    const generation = ++this.generation;
    this.request?.unsubscribe();
    this.loading = true;
    this.error = '';
    this.details = undefined;
    this.line = undefined;
    this.request = this.orders.getSupplyDetails(this.orderId).subscribe({
      next: details => {
        if (generation !== this.generation || !this.opened) return;
        this.details = details;
        this.line = findOrderSupplyLine(this.product, details);
        this.loading = false;
        if (!this.line) this.error = 'Позиция изменилась или больше не входит в заказ. Обновите данные.';
        this.rebuildSources();
        this.change.markForCheck();
        this.repositionAfterRender(generation);
      },
      error: () => {
        if (generation !== this.generation || !this.opened) return;
        this.loading = false;
        this.error = 'Не удалось загрузить поставку и отбор. Повторите попытку.';
        this.change.markForCheck();
        this.repositionAfterRender(generation);
      }
    });
    this.repositionAfterRender(generation);
  }

  date(value?: string | null): string {
    return deliveryDate(value)?.full || 'Не указано';
  }

  etaDate(value: string | null, precision?: DeliveryPrecision | null): string {
    return deliveryDate(value, precision)?.full || 'Не указан';
  }

  remaining(ordered: number, received: number): number {
    return Math.max(Number(ordered) - Number(received), 0);
  }

  supplierState(allocation: OrderSupplyAllocation) {
    const state = supplierSourceState(allocation);
    if (this.cancelled || this.details?.is_canceled) return { ...state, tone: 'muted', status: 'Заказ отменён' };
    if (this.line && this.remaining(this.line.required, this.line.fulfilled || 0) === 0) return { ...state, tone: 'muted', status: 'Позиция отгружена' };
    if (this.allocationAttributionPending && state.received < allocation.quantity) {
      return { ...state, tone: 'attention', status: 'Ожидание требует сверки' };
    }
    return state;
  }

  get allocationAttributionPending(): boolean {
    if (!this.line) return false;
    const externalRequired = Math.max(this.remaining(this.line.required, this.line.fulfilled || 0) - this.line.reserved, 0);
    const suppliers = this.line.allocations.filter(item => item.status !== 'CANCELLED' && item.status !== 'RECEIVED')
      .reduce((sum, item) => sum + this.remaining(item.quantity, item.physically_received_quantity ?? item.received_quantity), 0);
    const transfers = this.line.transfer_sources.filter(item => item.status !== 'CANCELLED')
      .reduce((sum, item) => sum + this.remaining(item.quantity, item.received_quantity), 0);
    return suppliers + Math.max(transfers, this.line.transfer_pending || 0) > externalRequired + .0001;
  }

  taskStatus(status: string): string {
    const labels: Record<string, string> = {
      OPEN: 'Ожидает отбора', IN_PROGRESS: 'В работе', DONE: 'Завершено', COMPLETED: 'Завершено',
      CANCELLED: 'Отменено', CANCELLED_BY_TRANSFER: 'Передано на перемещение', BLOCKED: 'Заблокировано',
      PICKED: 'Отобрано', PARTIALLY_PICKED: 'Отобрано частично', SHORT: 'Обнаружена нехватка'
    };
    return labels[status] || 'Статус требует уточнения';
  }

  handlingStatus(status: string): string {
    const labels: Record<string, string> = {
      IN_HANDS: 'В руках у сотрудника', PLACED: 'Размещено', RETURNED: 'Возвращено на полку',
      CANCELLED: 'Отменено', SHIPPED: 'Отгружено'
    };
    return labels[status] || 'Операция отбора';
  }

  placementStatus(status: string): string {
    const labels: Record<string, string> = {
      AVAILABLE: 'Готово к выдаче', RETURN_REQUIRED: 'Требуется вернуть на склад',
      CONFIRMED: 'Подтверждено', READY: 'Готово к выдаче', PENDING: 'Ожидает подтверждения',
      PENDING_RECEIPT: 'Ожидает оформления прихода', SHIPPED: 'Отгружено'
    };
    return labels[status] || 'Размещено';
  }

  receivingStatus(status: string): string {
    const labels: Record<string, string> = {
      WAIT_SOURCE: 'Требуется определить источник', WAIT_CELL: 'Ожидает размещения',
      REVIEW: 'Требуется проверить товар', REMOVAL_REQUIRED: 'Требуется убрать товар из ячейки',
      BUFFER: 'Товар в буфере приёмки'
    };
    return labels[status] || 'Ожидает оформления прихода';
  }

  pendingReceivingStatus(pending: OrderSupplyPendingReceiving): string {
    if (pending.unreconciled_quantity > 0 && pending.pending_quantity <= .0001) {
      return 'Ожидает оформления прихода';
    }
    return this.receivingStatus(pending.status);
  }

  trackSource(_: number, source: OrderSupplySourceView) { return source.id; }

  private rebuildSources() {
    if (!this.product) return;
    this.sources = orderSupplySources(this.product, this.details || this.supply,
      this.cancelled || !!this.details?.is_canceled, Date.now(), this.supplyError);
  }

  private repositionAfterRender(generation: number) {
    clearTimeout(this.positionTimer);
    // The lazy response can replace a short spinner with a tall panel. Measure
    // after Angular renders it so CDK can flip/push the popup into the viewport.
    this.positionTimer = setTimeout(() => {
      if (this.opened && generation === this.generation) this.connectedOverlay?.overlayRef?.updatePosition();
    }, 0);
  }

  ngOnDestroy() { this.close(false); }
}
